/**
 * ВЕНДОРЕННАЯ КОПИЯ. Оригинал — screenshot-interpretator/tools/overlay.js (соседний репозиторий
 * на сервере, /var/www/dev-cp/screenshot-interpretator). Скопирован, а не подключён пакетом,
 * потому что живёт в ДРУГОМ git-репозитории: импортировать его из CRA-сборки нечем.
 *
 * Правки сюда вносить НЕ НАДО — оригинал ведёт автор сервиса распознавания, и там же лежит
 * питоновский близнец (tools/overlay.py), с которым файл обязан совпадать по цветам и порядку
 * отрисовки. Обновление = повторное копирование целиком. Если правка всё же понадобилась —
 * сначала в оригинал, потом копией сюда, иначе следующее обновление её затрёт.
 *
 * Ниже — докблок оригинала без изменений.
 *
 * ---
 *
 * Отрисовка вывода click_targets поверх скриншота — то же самое, что делает tools/overlay.py,
 * только в браузере. Один и тот же кадр у бэка и у фронта должен выглядеть одинаково, поэтому
 * цвета и порядок отрисовки здесь намеренно повторяют питоновский рендерер: если правите тут,
 * правьте и там.
 *
 * Без зависимостей, ES-модуль.
 *
 *     import { renderOverlay } from "./overlay.js";
 *
 *     const stage = document.querySelector(".stage");   // position: relative
 *     stage.append(renderOverlay(scan));                // scan — ответ POST /scan
 *
 * Разметка вокруг нужна такая:
 *
 *     <div class="stage" style="position:relative">
 *       <img src="screen.png" style="display:block;width:100%">
 *       <!-- сюда встанет <svg>, он ляжет поверх и растянется по картинке -->
 *     </div>
 *
 * КЛЮЧЕВОЕ. Координаты в JSON — в пикселях ИСХОДНОГО кадра, а картинку в интерфейсе почти
 * всегда показывают в другом размере. Пересчитывать их вручную не нужно и не надо: у svg
 * ставится viewBox="0 0 image.width image.height", и браузер масштабирует сам. Ровно для этого
 * в выводе есть image.width / image.height.
 */

/**
 * Цвета по типу фигуры. Это те же значения, что в tools/overlay.py, переведённые из BGR
 * (порядок каналов OpenCV) в обычный RGB.
 */
export const KIND_COLORS = {
  rect: "#50dc50",
  rounded_rect: "#28c8ff",
  pill: "#c878ff",
  circle: "#ffa028",
  line: "#b4b4b4",
  other: "#ff5050",
};

export const TEXT_COLOR = "#ffffff";
export const CENTER_COLOR = "#ff0000";

const SVG_NS = "http://www.w3.org/2000/svg";

const DEFAULT_LAYERS = { shapes: true, text: true, centers: true, labels: false };

function createNode(name, attributes) {
  const node = document.createElementNS(SVG_NS, name);
  for (const [key, value] of Object.entries(attributes)) {
    node.setAttribute(key, String(value));
  }
  return node;
}

function shapeNode(shape, color, strokeWidth) {
  const [x0, y0, x1, y1] = shape.bbox;
  const width = x1 - x0;
  const height = y1 - y0;

  if (shape.kind === "circle") {
    return createNode("ellipse", {
      cx: x0 + width / 2, cy: y0 + height / 2, rx: width / 2, ry: height / 2,
      fill: "none", stroke: color, "stroke-width": strokeWidth,
    });
  }

  // pill — это скругление во всю короткую сторону; у прочих берём измеренный радиус.
  const radius = shape.kind === "pill" ? Math.min(width, height) / 2 : (shape.corner_radius || 0);
  return createNode("rect", {
    x: x0, y: y0, width, height, rx: radius, ry: radius,
    fill: "none", stroke: color, "stroke-width": strokeWidth,
  });
}

/**
 * Собирает <svg> с разметкой поверх кадра.
 *
 * @param {object} scan — ответ /scan целиком (нужны image, elements, shapes).
 * @param {object} [options]
 * @param {object} [options.layers] — какие слои рисовать: shapes, text, centers, labels.
 * @param {object} [options.colors] — переопределение цветов по типу фигуры.
 * @param {number} [options.strokeWidth] — толщина линий в пикселях ИСХОДНОГО кадра.
 * @param {function} [options.onSelect] — вызывается с объектом фигуры/элемента по клику.
 * @returns {SVGSVGElement}
 */
export function renderOverlay(scan, options = {}) {
  const layers = { ...DEFAULT_LAYERS, ...(options.layers || {}) };
  const colors = { ...KIND_COLORS, ...(options.colors || {}) };
  const { width, height } = scan.image;
  // Толщина в координатах кадра: на 720px это ~1.5px, на 1080px — те же полтора визуально.
  const strokeWidth = options.strokeWidth ?? Math.max(1.5, width / 480);

  const svg = createNode("svg", {
    viewBox: `0 0 ${width} ${height}`,
    preserveAspectRatio: "none",
    "aria-hidden": "true",
  });
  svg.style.position = "absolute";
  svg.style.inset = "0";
  svg.style.width = "100%";
  svg.style.height = "100%";

  if (layers.shapes) {
    // По убыванию площади: фигуры вложены друг в друга (кнопка внутри карточки), и если
    // рисовать в порядке чтения, крупная карточка ляжет поверх кнопки и скроет её.
    const ordered = [...(scan.shapes || [])].sort((a, b) => b.area - a.area);
    for (const shape of ordered) {
      const color = colors[shape.kind] || colors.other;
      const node = shapeNode(shape, color, strokeWidth);
      node.dataset.id = shape.id;
      node.dataset.kind = shape.kind;
      if (options.onSelect) {
        node.style.pointerEvents = "stroke";
        node.style.cursor = "pointer";
        node.addEventListener("click", () => options.onSelect(shape));
      }
      svg.append(node);

      if (layers.labels) {
        const fontSize = Math.round(width / 55);
        const label = createNode("text", {
          x: shape.bbox[0] + strokeWidth,
          // Подпись ставится над рамкой, но не выше края кадра — иначе у фигур в самом верху
          // (статус-бар, шапка) она уезжает за пределы viewBox и не видна.
          y: Math.max(fontSize, shape.bbox[1] - strokeWidth * 2),
          fill: color, "font-size": fontSize, "font-family": "monospace",
        });
        label.textContent = `${shape.id}:${shape.kind}`;
        svg.append(label);
      }
    }
  }

  if (layers.text) {
    for (const element of scan.elements || []) {
      const [x0, y0, x1, y1] = element.bbox;
      const node = createNode("rect", {
        x: x0, y: y0, width: x1 - x0, height: y1 - y0,
        fill: "none", stroke: TEXT_COLOR, "stroke-width": strokeWidth * 0.7,
      });
      node.dataset.id = element.id;
      if (options.onSelect) {
        node.style.pointerEvents = "stroke";
        node.style.cursor = "pointer";
        node.addEventListener("click", () => options.onSelect(element));
      }
      svg.append(node);
    }
  }

  if (layers.centers) {
    // Точка клика бота — рисуется последней, чтобы её не перекрывали рамки.
    for (const element of scan.elements || []) {
      svg.append(createNode("circle", {
        cx: element.center[0], cy: element.center[1],
        r: strokeWidth * 2.2, fill: CENTER_COLOR,
      }));
    }
  }

  return svg;
}

/**
 * Подсветить объект по id (например, при наведении на строку в списке рядом с картинкой).
 * Передайте null, чтобы снять подсветку со всех.
 */
export function highlight(svg, id, factor = 2.8) {
  for (const node of svg.querySelectorAll("[data-id]")) {
    const base = Number(node.dataset.baseStroke || node.getAttribute("stroke-width"));
    node.dataset.baseStroke = String(base);
    node.setAttribute("stroke-width", node.dataset.id === id ? base * factor : base);
  }
}
